import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import type { PublisherSortDirection, PublisherSortProperty, PublisherStatus } from './model';
import {
  parsePublishersRouteSearch,
  serializePublishersRouteSearch,
} from './publishersRouteSearch';

export function usePublishersRouteFilters() {
  const navigate = useNavigate({ from: '/supply/publishers' });
  const search = useSearch({ from: '/supply/publishers' });
  const filter = useMemo(() => parsePublishersRouteSearch(search), [search]);
  const update = useCallback(
    (next: typeof filter) => {
      void navigate({ search: serializePublishersRouteSearch(next), replace: true });
    },
    [navigate],
  );
  const setSearch = useCallback(
    (searchTerm: string) => {
      update({ ...filter, searchTerm, page: 0 });
    },
    [filter, update],
  );
  const setStatuses = useCallback(
    (statuses: PublisherStatus[]) => {
      update({ ...filter, statuses, page: 0 });
    },
    [filter, update],
  );
  const setPagination = useCallback(
    (page: number, size: number) => {
      update({ ...filter, page, size });
    },
    [filter, update],
  );
  const setSort = useCallback(
    (sortProperty: PublisherSortProperty | null, sortDirection: PublisherSortDirection) => {
      update({ ...filter, sortProperty, sortDirection, page: 0 });
    },
    [filter, update],
  );
  return {
    filter,
    setSearch,
    setStatuses,
    setPagination,
    setSort,
  };
}
